class TalisloomMenu extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.toggleButton = this.querySelector('[data-menu-toggle]');
    this.closeButtons = this.querySelectorAll('[data-menu-close]');
    this.menuLinks = this.querySelectorAll('[data-menu-link]');
    this.panel = this.querySelector('[data-menu-panel]');
    if (!this.toggleButton || !this.panel) return;

    this.handleToggleClick = this.open.bind(this);
    this.handleCloseClick = this.close.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleDesktopChange = this.onDesktopChange.bind(this);
    this.desktopMedia = window.matchMedia('(min-width: 750px)');

    this.toggleButton.addEventListener('click', this.handleToggleClick);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.handleCloseClick));
    this.menuLinks.forEach((link) => link.addEventListener('click', this.handleCloseClick));
    this.addEventListener('keydown', this.handleKeydown);
    if (this.desktopMedia.addEventListener) {
      this.desktopMedia.addEventListener('change', this.handleDesktopChange);
    } else {
      this.desktopMedia.addListener?.(this.handleDesktopChange);
    }

    this.initialized = true;
    this.syncState(false);
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.toggleButton.removeEventListener('click', this.handleToggleClick);
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.handleCloseClick));
    this.menuLinks.forEach((link) => link.removeEventListener('click', this.handleCloseClick));
    this.removeEventListener('keydown', this.handleKeydown);
    if (this.desktopMedia.removeEventListener) {
      this.desktopMedia.removeEventListener('change', this.handleDesktopChange);
    } else {
      this.desktopMedia.removeListener?.(this.handleDesktopChange);
    }
    this.close({ restoreFocus: false });
    this.initialized = false;
  }

  open() {
    this.setAttribute('open', '');
    this.setBackgroundInert(true);
    this.syncState(true);
    document.documentElement.classList.add('tl-menu-open');
    this.getFocusableElements()[0]?.focus();
  }

  close({ restoreFocus = true } = {}) {
    const wasOpen = this.hasAttribute('open');
    this.removeAttribute('open');
    this.syncState(false);
    this.setBackgroundInert(false);
    document.documentElement.classList.remove('tl-menu-open');
    if (wasOpen && restoreFocus) this.toggleButton.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.hasAttribute('open')) return;

    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onDesktopChange(event) {
    if (event.matches) this.close({ restoreFocus: false });
  }

  getFocusableElements() {
    const visiblePanel = this.panel.querySelector('.tl-mobile-menu__panel');
    if (!visiblePanel) return [];
    return Array.from(visiblePanel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.backgroundInertState = new Map();
      const targets = [
        ...document.querySelectorAll('[data-menu-global-background], main, footer'),
        ...this.querySelectorAll('[data-menu-background]'),
      ];
      targets.forEach((target) => {
        if (this.backgroundInertState.has(target)) return;
        this.backgroundInertState.set(target, target.inert);
        target.inert = true;
      });
      return;
    }

    this.backgroundInertState?.forEach((wasInert, target) => { target.inert = wasInert; });
    this.backgroundInertState = null;
  }

  syncState(isOpen) {
    this.toggleButton.setAttribute('aria-expanded', String(isOpen));
    this.panel.setAttribute('aria-hidden', String(!isOpen));
    this.panel.inert = !isOpen;
  }
}

class TalisloomSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.track = this.querySelector('[data-slider-track]');
    this.previousButton = this.querySelector('[data-slider-previous]');
    this.nextButton = this.querySelector('[data-slider-next]');
    this.progress = this.querySelector('[data-slider-progress]');
    this.progressThumb = this.progress?.querySelector('.tl-slider__progress-thumb');
    if (!this.track) return;

    this.handlePreviousClick = () => this.scrollByPage(-1);
    this.handleNextClick = () => this.scrollByPage(1);
    this.handleScroll = this.updateControls.bind(this);
    this.handleResize = this.updateControls.bind(this);
    this.handlePointerDown = this.startDrag.bind(this);
    this.handlePointerMove = this.drag.bind(this);
    this.handlePointerUp = this.stopDrag.bind(this);
    this.handleClickAfterDrag = this.preventClickAfterDrag.bind(this);
    this.handleProgressPointerDown = this.startProgressDrag.bind(this);
    this.handleProgressPointerMove = this.dragProgress.bind(this);
    this.handleProgressPointerUp = this.stopProgressDrag.bind(this);

    this.previousButton?.addEventListener('click', this.handlePreviousClick);
    this.nextButton?.addEventListener('click', this.handleNextClick);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerUp);
    this.track.addEventListener('click', this.handleClickAfterDrag);
    this.progress?.addEventListener('pointerdown', this.handleProgressPointerDown);
    this.progress?.addEventListener('pointermove', this.handleProgressPointerMove);
    this.progress?.addEventListener('pointerup', this.handleProgressPointerUp);
    this.progress?.addEventListener('pointercancel', this.handleProgressPointerUp);
    this.progress?.addEventListener('lostpointercapture', this.handleProgressPointerUp);
    this.initialized = true;
    this.updateControls();
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.previousButton?.removeEventListener('click', this.handlePreviousClick);
    this.nextButton?.removeEventListener('click', this.handleNextClick);
    this.track.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    this.track.removeEventListener('pointerdown', this.handlePointerDown);
    this.track.removeEventListener('pointermove', this.handlePointerMove);
    this.track.removeEventListener('pointerup', this.handlePointerUp);
    this.track.removeEventListener('pointercancel', this.handlePointerUp);
    this.track.removeEventListener('click', this.handleClickAfterDrag);
    this.progress?.removeEventListener('pointerdown', this.handleProgressPointerDown);
    this.progress?.removeEventListener('pointermove', this.handleProgressPointerMove);
    this.progress?.removeEventListener('pointerup', this.handleProgressPointerUp);
    this.progress?.removeEventListener('pointercancel', this.handleProgressPointerUp);
    this.progress?.removeEventListener('lostpointercapture', this.handleProgressPointerUp);
    this.stopProgressDrag();
    this.stopDrag();
    this.initialized = false;
  }

  scrollByPage(direction) {
    this.track.scrollBy({ left: this.track.clientWidth * 0.82 * direction, behavior: 'smooth' });
  }

  updateControls() {
    const maximum = this.track.scrollWidth - this.track.clientWidth;
    if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maximum - 2;

    if (this.progress && this.progressThumb) {
      const hasOverflow = maximum > 2;
      this.progress.hidden = !hasOverflow;
      if (hasOverflow) {
        const visibleRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
        this.progressThumb.style.width = `${visibleRatio * 100}%`;
        this.progressThumb.style.left = `${(this.track.scrollLeft / this.track.scrollWidth) * 100}%`;
      }
    }
  }

  startProgressDrag(event) {
    if (!this.progress || !this.progressThumb || this.track.scrollWidth <= this.track.clientWidth) return;
    this.progressDragging = true;
    this.progress.setPointerCapture(event.pointerId);
    this.updateProgressFromPointer(event);
    event.preventDefault();
  }

  dragProgress(event) {
    if (!this.progressDragging) return;
    this.updateProgressFromPointer(event);
    event.preventDefault();
  }

  stopProgressDrag(event) {
    if (!this.progressDragging) return;
    this.progressDragging = false;
    if (event?.pointerId != null && this.progress?.hasPointerCapture?.(event.pointerId)) {
      this.progress.releasePointerCapture(event.pointerId);
    }
  }

  updateProgressFromPointer(event) {
    const progressBounds = this.progress.getBoundingClientRect();
    const thumbWidth = this.progressThumb.getBoundingClientRect().width;
    const usableWidth = Math.max(0, progressBounds.width - thumbWidth);
    const pointerOffset = event.clientX - progressBounds.left - thumbWidth / 2;
    const ratio = usableWidth === 0 ? 0 : Math.min(1, Math.max(0, pointerOffset / usableWidth));
    this.track.scrollLeft = (this.track.scrollWidth - this.track.clientWidth) * ratio;
  }

  startDrag(event) {
    if (event.pointerType === 'touch' || event.target?.closest?.('button, a')) return;
    this.dragPending = true;
    this.dragging = false;
    this.dragMoved = false;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.track.scrollLeft;
    this.track.setPointerCapture(event.pointerId);
  }

  drag(event) {
    if (!this.dragPending) return;
    const delta = event.clientX - this.dragStartX;
    if (Math.abs(delta) < 6) return;
    this.dragging = true;
    this.dragMoved = true;
    event.preventDefault();
    this.track.scrollLeft = this.dragStartScrollLeft - delta;
  }

  stopDrag() {
    if (this.dragMoved) this.clickSuppressed = true;
    this.dragPending = false;
    this.dragging = false;
  }

  preventClickAfterDrag(event) {
    if (!this.clickSuppressed) return;
    event.preventDefault();
    event.stopPropagation();
    this.clickSuppressed = false;
  }
}

class TalisloomProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.state = this.querySelector('[data-variant-state]');
    this.variantSelect = this.querySelector('[data-variant-select]');
    this.price = this.querySelector('[data-product-price]');
    this.compare = this.querySelector('[data-product-compare]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.data = this.querySelector('[data-product-variants]');
    if (!this.state || !this.variantSelect || !this.data) return;

    try {
      this.variants = JSON.parse(this.data.textContent);
    } catch {
      this.variants = [];
    }
    this.handleVariantChange = this.syncVariant.bind(this);
    this.variantSelect.addEventListener('change', this.handleVariantChange);
    this.initialized = true;
    this.syncVariant();
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.variantSelect.removeEventListener('change', this.handleVariantChange);
    this.initialized = false;
  }

  syncVariant() {
    const selectedId = Number(this.variantSelect.value);
    const selectedVariant = this.variants.find((variant) => Number(variant.id) === selectedId);
    if (!selectedVariant) return;

    this.selectedVariant = selectedVariant;
    this.state.dataset.variantId = String(selectedVariant.id);
    if (this.price) this.price.textContent = this.formatMoney(selectedVariant.price);
    if (this.compare) {
      const hasCompare = selectedVariant.compare_at_price > selectedVariant.price;
      this.compare.hidden = !hasCompare;
      this.compare.textContent = hasCompare ? this.formatMoney(selectedVariant.compare_at_price) : '';
    }
    if (this.addButton) {
      this.addButton.disabled = !selectedVariant.available;
      this.addButton.textContent = selectedVariant.available ? this.addButton.dataset.addLabel : this.addButton.dataset.soldOutLabel;
    }

    const mediaId = selectedVariant.featured_media?.id || selectedVariant.featured_image?.id;
    if (mediaId) this.querySelector(`[data-media-id="${mediaId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  formatMoney(cents) {
    const currency = this.state.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
    } catch {
      return String(cents);
    }
  }
}

class TalisloomProductMediaModal extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.dialog = this.querySelector('[data-product-media-modal]');
    this.image = this.querySelector('[data-media-modal-image]');
    this.closeButton = this.querySelector('[data-media-modal-close]');
    this.previousButton = this.querySelector('[data-media-modal-previous]');
    this.nextButton = this.querySelector('[data-media-modal-next]');
    this.product = this.closest('talisloom-product');
    this.triggers = this.product ? Array.from(this.product.querySelectorAll('[data-media-modal-trigger]')) : [];
    if (!this.dialog || !this.image || !this.closeButton || !this.previousButton || !this.nextButton || this.triggers.length === 0) return;

    this.handleTriggerClick = (event) => this.open(event.currentTarget);
    this.handleCloseClick = () => this.close();
    this.handlePreviousClick = () => this.showRelative(-1);
    this.handleNextClick = () => this.showRelative(1);
    this.handleCancel = (event) => {
      event.preventDefault();
      this.close();
    };
    this.handleBackdropClick = (event) => {
      if (event.target === this.dialog) this.close();
    };
    this.handleDialogClose = () => {
      this.dialog.toggleAttribute('scroll-lock', false);
      this.restoreFocus();
    };

    this.triggers.forEach((trigger) => trigger.addEventListener('click', this.handleTriggerClick));
    this.closeButton.addEventListener('click', this.handleCloseClick);
    this.previousButton.addEventListener('click', this.handlePreviousClick);
    this.nextButton.addEventListener('click', this.handleNextClick);
    this.dialog.addEventListener('cancel', this.handleCancel);
    this.dialog.addEventListener('click', this.handleBackdropClick);
    this.dialog.addEventListener('close', this.handleDialogClose);
    const hasNavigation = this.triggers.length > 1;
    this.previousButton.hidden = !hasNavigation;
    this.nextButton.hidden = !hasNavigation;
    this.initialized = true;
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.handleTriggerClick));
    this.closeButton.removeEventListener('click', this.handleCloseClick);
    this.previousButton.removeEventListener('click', this.handlePreviousClick);
    this.nextButton.removeEventListener('click', this.handleNextClick);
    this.dialog.removeEventListener('cancel', this.handleCancel);
    this.dialog.removeEventListener('click', this.handleBackdropClick);
    this.dialog.removeEventListener('close', this.handleDialogClose);
    this.initialized = false;
  }

  open(trigger) {
    const source = trigger.dataset.mediaModalSrc;
    if (!source || this.dialog.open) return;

    this.updateMedia(trigger);
    this.dialog.showModal();
    this.dialog.toggleAttribute('scroll-lock', true);
    this.closeButton.focus();
  }

  showRelative(step) {
    if (!this.activeTrigger || this.triggers.length < 2) return;

    const currentIndex = this.triggers.indexOf(this.activeTrigger);
    const nextIndex = (currentIndex + step + this.triggers.length) % this.triggers.length;
    this.updateMedia(this.triggers[nextIndex]);
  }

  updateMedia(trigger) {
    const source = trigger?.dataset.mediaModalSrc;
    if (!source) return;

    this.activeTrigger = trigger;
    this.image.src = source;
    this.image.alt = trigger.dataset.mediaModalAlt || '';
  }

  close() {
    if (this.dialog.open) this.dialog.close();
    else {
      this.dialog.toggleAttribute('scroll-lock', false);
      this.restoreFocus();
    }
  }

  restoreFocus() {
    const trigger = this.activeTrigger;
    this.activeTrigger = null;
    trigger?.focus();
  }
}

if (!customElements.get('talisloom-menu')) customElements.define('talisloom-menu', TalisloomMenu);
if (!customElements.get('talisloom-slider')) customElements.define('talisloom-slider', TalisloomSlider);
if (!customElements.get('talisloom-product')) customElements.define('talisloom-product', TalisloomProduct);
if (!customElements.get('talisloom-product-media-modal')) customElements.define('talisloom-product-media-modal', TalisloomProductMediaModal);
